// 3rd
import Vue from 'vue';
import VueRouter from 'vue-router';
import goTo from 'vuetify/es5/services/goto';
import moment from 'moment';
// store
import Store from '../store/index';
// models
import { User as UserModel } from '../models';
// views
import Impersonate from '../views/Impersonate';
import MyOrders from '../views/MyOrders';
import OrderItemDetail from '../views/order/OrderItemDetail';
import Dashboard from '../views/Home';
import Contact from '../views/Contact';
import Rank from '../views/Rank';
import Shop from '../views/Shop';
import Product from '../views/shop/Product';
import Cart from '../views/shop/Cart';
import Checkout from '../views/shop/Checkout';
import Account from '../views/Account';
import Profile from '../views/Profile';
import ChangeRetailer from '../views/profile/ChangeRetailer';
import ChangeRetailerSuccess from '../views/profile/ChangeRetailerSuccess';
import Avatar from '../views/profile/Avatar';
import Data from '../views/profile/Data';
import CompanyData from '../views/profile/CompanyData';
import Approvals from '../views/account/Approvals';
import AddContract from '../views/account/AddContract';
import News from '../views/News';
import NewsDetails from '../views/news/Details';
import Wishlist from '../views/Wishlist';
import Confirmation from '../views/Confirmation';
import StaticPage from '../views/StaticPage';
import Auth from '../views/Auth';
import Login from '../views/auth/Login';
import ResetPassword from '../views/auth/ResetPassword';
import RegisterCompany from '../views/auth/RegisterCompany';
import RegisterParticipant from '../views/auth/RegisterParticipant';
import AuthConfirmation from '../views/auth/AuthConfirmation';
import Statistic from '../views/Statistic';
import Mock from '../views/Mock';
// utils
import {
    LOCAL_STORAGE_DATA_INVALIDATION,
    LOCAL_STORAGE_JWT,
    LOCAL_STORAGE_TOKEN_EXPIRES,
    LOCAL_STORAGE_USER,
    ROUTE_IMPERSONATE,
    ROUTE_ACCOUNT,
    ROUTE_ADD_CONTRACT,
    ROUTE_APPROVALS,
    ROUTE_CART,
    ROUTE_CHANGE_AVATAR,
    ROUTE_CHANGE_DATA,
    ROUTE_CHECKOUT,
    ROUTE_CONTACT,
    ROUTE_DASHBOARD,
    ROUTE_LOGIN,
    ROUTE_LOGIN_DEEPLINK,
    ROUTE_PRODUCT_DETAILS,
    ROUTE_PROFILE,
    ROUTE_RANK,
    ROUTE_RANG,
    ROUTE_SHOP,
    ROUTE_STATIC_PAGE,
    ROUTE_STATISTIC_DETAIL,
    ROUTE_WISHLIST,
    ROUTE_CHANGE_RETAILER,
    ROUTE_CHANGE_RETAILER_SUCCESS,
    ROUTE_MY_ORDERS,
    ROUTE_ORDER_DETAILS,
    ROUTE_CHANGE_COMPANY_DATA
} from '../utils/constants';
import { logOutUser, redirect } from '../utils/helper';
import env from '../utils/env';

Vue.use(VueRouter);

// Meta options
// requiresAuth - if user is not logged in redirect to login page
// showLanguageSwitch - language switch on top right corner
// showFooterWithLinks - imprint and data protection footer (login and register forms)
// showBackToLogin - back to login (for register forms)
// showBottomNav - shows the app main navigation (only for logged in users)
// showTopBar - shows the top bar with navigation drawer (only for logged in users)
// fillContentHeight - fill content height and center vertically

const routes = [
    {
        path: '/',
        redirect: { name: ROUTE_DASHBOARD }
    },
    {
        path: '/app',
        beforeEnter: (to, _, next) => {
            const path = to.query.path;
            if (path) {
                next({ path: `/${path}` });
            } else {
                next({ path: `/` });
            }
        }
    },
    {
        path: '/my-orders',
        name: ROUTE_MY_ORDERS,
        component: MyOrders,
        meta: {
            title: 'my-orders',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/my-orders/:orderItemId',
        name: ROUTE_ORDER_DETAILS,
        component: OrderItemDetail,
        props: true,
        meta: {
            title: 'my-orders-feedback',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/dashboard',
        name: ROUTE_DASHBOARD,
        component: Dashboard,
        meta: {
            title: 'dashboard',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/impersonate/:token/:expire/:userId/:target/:targetId',
        name: ROUTE_IMPERSONATE,
        component: Impersonate,
        props: true,
        meta: {
            noReferer: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/static/:slug',
        name: ROUTE_STATIC_PAGE,
        component: StaticPage,
        props: true,
        meta: {
            title: null,
            titleType: 'static',
            noReferer: true,
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/contact',
        name: ROUTE_CONTACT,
        component: Contact,
        meta: {
            title: 'contact',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true,
            noReferer: true
        }
    },
    {
        path: '/statistic',
        name: ROUTE_STATISTIC_DETAIL,
        component: Statistic,
        meta: {
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true,
            noReferer: true
        }
    },
    {
        path: '/shop',
        name: ROUTE_SHOP,
        component: Shop,
        meta: {
            title: 'shop',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/wishlist',
        name: ROUTE_WISHLIST,
        component: Wishlist,
        meta: {
            title: 'wishlist',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/shop/:productId',
        name: ROUTE_PRODUCT_DETAILS,
        component: Product,
        props: true,
        meta: {
            title: 'shop',
            titleType: 'product',
            noReferer: true,
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/rank',
        name: ROUTE_RANK,
        component: Rank,
        props: true,
        meta: {
            title: 'rank',
            noReferer: true,
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/rang',
        redirect: ROUTE_RANK,
        name: ROUTE_RANG
    },
    {
        path: '/profile',
        name: ROUTE_PROFILE,
        component: Profile,
        meta: {
            title: 'profile',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/profile/change-data',
        name: ROUTE_CHANGE_DATA,
        component: Data,
        props: true,
        meta: {
            title: 'profile_change_data',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/profile/change-company-data',
        name: ROUTE_CHANGE_COMPANY_DATA,
        component: CompanyData,
        props: true,
        meta: {
            title: 'profile_change_company_data',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/profile/change-avatar',
        name: ROUTE_CHANGE_AVATAR,
        component: Avatar,
        meta: {
            title: 'profile_change_avatar',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/profile/change-retailer',
        name: ROUTE_CHANGE_RETAILER,
        component: ChangeRetailer,
        meta: {
            title: 'profile_change_retailer',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/profile/change-retailer/success',
        name: ROUTE_CHANGE_RETAILER_SUCCESS,
        component: ChangeRetailerSuccess,
        meta: {
            title: 'profile_change_retailer_success',
            requiresAuth: false,
            showBottomNav: false,
            showTopBar: false
        }
    },
    {
        path: '/account',
        name: ROUTE_ACCOUNT,
        component: Account,
        meta: {
            title: 'account',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/add-contract',
        name: ROUTE_ADD_CONTRACT,
        component: AddContract,
        meta: {
            title: 'add_contract',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true,
            noReferer: true
        }
    },
    {
        path: '/approvals',
        name: ROUTE_APPROVALS,
        component: Approvals,
        meta: {
            title: 'approvals',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/cart',
        name: ROUTE_CART,
        component: Cart,
        meta: {
            title: 'cart',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/checkout',
        name: ROUTE_CHECKOUT,
        component: Checkout,
        meta: {
            title: 'checkout',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/news',
        name: 'news',
        component: News,
        meta: {
            title: 'news',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/news/:newsId',
        name: 'news-details',
        component: NewsDetails,
        props: true,
        meta: {
            title: 'news',
            titleType: 'news',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true
        }
    },
    {
        path: '/confirmation',
        name: 'confirmation',
        component: Confirmation,
        meta: {
            title: 'confirmation',
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true,
            noReferer: true
        }
    },
    // Auth
    {
        path: '/auth',
        name: 'auth',
        component: Auth,
        children: [
            {
                path: '/login',
                name: ROUTE_LOGIN,
                component: Login,
                meta: {
                    title: 'login',
                    showFooterWithLinks: true,
                    showLanguageSwitch: true,
                    fillContentHeight: true,
                    guestOnly: true
                }
            },
            {
                path: '/login/:modal',
                name: ROUTE_LOGIN_DEEPLINK,
                component: Login,
                props: true,
                meta: {
                    showFooterWithLinks: true,
                    showLanguageSwitch: true,
                    fillContentHeight: true,
                    guestOnly: true
                }
            },
            {
                path: '/register-company',
                name: 'register-company',
                component: RegisterCompany,
                meta: {
                    title: 'register_company',
                    showFooterWithLinks: true,
                    showBackToLogin: true,
                    fillContentHeight: true
                }
            },
            {
                path: '/register-company/:token',
                name: 'register-company-proceed',
                component: RegisterCompany,
                props: true,
                meta: {
                    title: 'register_company',
                    showFooterWithLinks: true,
                    showBackToLogin: true,
                    fillContentHeight: true
                }
            },
            {
                path: '/register-participant',
                name: 'register-participant',
                component: RegisterParticipant,
                meta: {
                    title: 'register_participant',
                    showFooterWithLinks: true,
                    showBackToLogin: true,
                    fillContentHeight: true
                }
            },
            {
                path: '/register-confirmation',
                name: 'register-confirmation',
                component: AuthConfirmation,
                meta: {
                    title: 'auth_confirmation',
                    showFooterWithLinks: true,
                    showBackToLogin: true,
                    fillContentHeight: true,
                    noReferer: true
                }
            },
            {
                path: '/reset-password',
                name: 'reset-password',
                component: ResetPassword,
                meta: {
                    title: 'reset_password',
                    showFooterWithLinks: true,
                    fillContentHeight: true,
                    showBackToLogin: true,
                    showLanguageSwitch: true
                }
            }
        ]
    }
];

if (process.env.NODE_ENV === 'development') {
    routes.push({
        path: '/mock',
        name: 'mock',
        component: Mock,
        meta: {
            requiresAuth: true,
            showBottomNav: true,
            showTopBar: true,
            noReferer: true
        }
    });
}

const router = new VueRouter({
    scrollBehavior: (to, from, savedPosition) => {
        const storeRef = Store._modules.root.state.referer;
        const storeRefY = Store._modules.root.state.refererY;
        let scrollTo = 0;
        if (storeRef === to.path && storeRefY > 0) {
            Store.dispatch('set', { item: 'refererY', value: 0 });
            return storeRefY;
        } else if (to.hash) {
            return to.hash;
        } else if (savedPosition) {
            return savedPosition;
        }
        return goTo(scrollTo);
    },
    routes
});

// auth check
router.beforeEach(async (to, from, next) => {
    // check for data invalidation
    if (
        localStorage.getItem(LOCAL_STORAGE_DATA_INVALIDATION) &&
        Number(localStorage.getItem(LOCAL_STORAGE_DATA_INVALIDATION)) +
            Number(env('data-invalidation')) * 60 <=
            moment().unix()
    ) {
        await Store.dispatch('set', { item: 'cacheInvalidation', value: true });
        await Store.dispatch('set', { item: 'init', value: false });
        await Store.dispatch('set', { item: 'bootstrap', value: false });
        await Store.dispatch('fetchBootstrap');
        await Store.dispatch('fetchInit', { skipRedirects: true });
        await Store.dispatch('set', {
            item: 'cacheInvalidation',
            value: false
        });
    }

    if (!from.meta.noReferer) {
        Store.dispatch('setRefererRoute', from.path);
        Store.dispatch('set', { item: 'refererY', value: window.scrollY });
    }

    if (to.matched.some(record => record.meta.requiresAuth)) {
        const expiresIn = moment(
            localStorage.getItem(LOCAL_STORAGE_TOKEN_EXPIRES)
        ).format('x');
        const now = moment().format('x');

        if (expiresIn < now) {
            await UserModel.refreshToken();
        }

        if (
            localStorage.getItem(LOCAL_STORAGE_JWT) == null ||
            localStorage.getItem(LOCAL_STORAGE_USER) == null
        ) {
            logOutUser(true);
        } else {
            next();
        }
    } else {
        if (
            to.matched.some(record => record.meta.guestOnly) &&
            localStorage.getItem(LOCAL_STORAGE_JWT) != null &&
            localStorage.getItem(LOCAL_STORAGE_USER) != null
        ) {
            redirect(ROUTE_DASHBOARD);
        } else {
            next();
        }
    }
});

export default router;
